import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LijnGrafiek } from '../components/charts';
import {
  Avatar,
  Bevestig,
  Cel,
  Kaart,
  KaartKop,
  Knop,
  Leeg,
  Modaal,
  PaginaKop,
  Paginering,
  Plek,
  Rij,
  SkeletTabel,
  SkeletTegels,
  Tabel,
  Tegel,
  Veld,
  WedstrijdRegel,
  useToast
} from '../components/ui';
import { IcoonBeker, IcoonKruis } from '../components/icons';
import { SERIES } from '../lib/colors';
import { useAuth } from '../lib/auth';
import { START_RATING, chronologisch, deeltPlek, huidigSeizoen, ranglijst as maakRanglijst, plekken, seizoenLabel, seizoensStand, standSleutel } from '../lib/elo';
import { fmtDatum, initialen, relatief, toonNaam, toonRating } from '../lib/format';
import {
  useLeague,
  useNodigUit,
  usePouleLeden,
  usePoules,
  useProfiles,
  useUitnodigingen,
  useVerwijderLid,
  useVerwijderPoule,
  useWijzigPoule,
  useZetBijnaam,
  useZetFavoriet,
  useZetLidRol
} from '../lib/queries';

type Tab = 'deelnemers' | 'bijnamen' | 'uitnodigen';

/* Tien regels is wat er zonder scrollen op een dashboardkaart past. */
const STAND_PER_BLADZIJDE = 10;

/**
 * Het poule-overzicht is een dashboard: hoe staat deze competitie ervoor.
 * De deelnemers staan als klein paneel rechts — je wilt weten wie er meedoen,
 * maar het is niet waar je voor komt.
 */
export default function PouleDetail() {
  const { id = '' } = useParams();
  const navigeer = useNavigate();
  const { profile } = useAuth();
  const poules = usePoules();
  const leden = usePouleLeden();
  const uitnodigingen = useUitnodigingen();
  const profielen = useProfiles();
  const { players, matches, elo, zoekSpeler, laden } = useLeague(id);
  const meld = useToast();

  const wijzigen = useWijzigPoule();
  const verwijderen = useVerwijderPoule();
  const favoriet = useZetFavoriet();

  const [tab, setTab] = useState<Tab>('deelnemers');
  const [standPagina, setStandPagina] = useState(0);
  const [bewerken, setBewerken] = useState(false);
  const [weg, setWeg] = useState(false);

  const poule = (poules.data ?? []).find((p) => p.id === id);
  const eigenLeden = (leden.data ?? []).filter((l) => l.pouleId === id);
  const mijnLidmaatschap = eigenLeden.find((l) => l.profielId === profile?.id);
  const ikBenBeheerder = mijnLidmaatschap?.rol === 'beheerder';
  const openUitnodigingen = (uitnodigingen.data ?? []).filter((u) => u.pouleId === id && u.status === 'open');

  const naamVan = (profielId: string) =>
    (profielen.data ?? []).find((p) => p.id === profielId)?.name ?? zoekSpeler(profielId).name;

  if (poules.isLoading || laden) {
    return (
      <>
        <PaginaKop titel="Poule" />
        <SkeletTegels />
        <div className="mt-3.5">
          <Kaart>
            <SkeletTabel rijen={5} kolommen={4} />
          </Kaart>
        </div>
      </>
    );
  }

  if (!poule) {
    return (
      <>
        <PaginaKop titel="Poule" />
        <Kaart>
          <Leeg tekst="Deze poule bestaat niet, of je doet er niet aan mee." ikoon="poule" />
        </Kaart>
      </>
    );
  }

  /* alleen de leden van deze poule tellen mee in de stand */
  const deelnemers = players.filter((p) => eigenLeden.some((l) => l.profielId === p.id));
  const stand = maakRanglijst(deelnemers, matches, elo.ratings);
  const laatste = chronologisch(matches).reverse();
  const seizoen = huidigSeizoen();
  const seizoensRijen = seizoensStand(seizoen, matches, zoekSpeler);
  /* Gelijke rating deelt de plek; over de hele stand rekenen, niet over het
     afgekapte lijstje. */
  const plaatsen = plekken(stand, standSleutel);
  const plaatsenSeizoen = plekken(seizoensRijen, standSleutel);
  const mijnIndex = stand.findIndex((r) => r.player.id === profile?.id);
  const mijnPlek = mijnIndex < 0 ? 0 : plaatsen[mijnIndex];

  /* De bladzijde kan buiten bereik raken als er iemand uit de poule gaat. */
  const standBladzijden = Math.max(1, Math.ceil(stand.length / STAND_PER_BLADZIJDE));
  const standBlad = Math.min(standPagina, standBladzijden - 1);
  const standBegin = standBlad * STAND_PER_BLADZIJDE;

  const doelpunten = matches.reduce((n, m) => n + m.scoreA + m.scoreB, 0);

  /* ratingverloop van de bovenste vier in deze poule */
  const reeksen = stand
    .slice(0, 4)
    .map((r, i) => ({
      naam: r.player.name,
      kleur: SERIES[i],
      punten: (elo.verloop[r.player.id] ?? []).map((v) => ({ x: new Date(v.at).getTime(), y: v.rating }))
    }))
    .filter((s) => s.punten.length > 1);

  return (
    <>
      <PaginaKop
        titel={poule.naam}
        onder={poule.omschrijving ?? undefined}
        rechts={
          <>
            <Knop
              onClick={() =>
                void favoriet.mutateAsync({
                  pouleId: id,
                  favoriet: !mijnLidmaatschap?.favoriet
                })
              }
              title={mijnLidmaatschap?.favoriet ? 'Uit favorieten halen' : 'Als favoriet markeren'}
            >
              <span className={mijnLidmaatschap?.favoriet ? 'text-goud' : 'text-krijt-400'}>★</span>
              {mijnLidmaatschap?.favoriet ? 'Favoriet' : 'Favoriet maken'}
            </Knop>
            {ikBenBeheerder ? (
              <>
                <Knop onClick={() => setBewerken(true)}>Bewerken</Knop>
                <Knop variant="gevaar" onClick={() => setWeg(true)}>
                  Verwijderen
                </Knop>
              </>
            ) : null}
          </>
        }
      />

      {poule.prijs ? (
        <div className="mb-3.5 flex items-center gap-3 border-2 border-krijt-900 bg-signaal/30 px-4 py-3">
          <IcoonBeker className="!h-7 !w-7 text-krijt-900" />
          <div>
            <p className="opschrift text-krijt-600">Er wordt gespeeld om</p>
            <p className="kop text-[18px]">{poule.prijs}</p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3.5 lg:grid-cols-[1fr_300px]">
        {/* ---------- dashboard ---------- */}
        <div className="flex flex-col gap-3.5">
          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <Tegel
              label="Potjes"
              waarde={matches.length}
              onder={laatste.length ? `laatste: ${relatief(laatste[0].playedAt)}` : 'nog niet gespeeld'}
            />
            <Tegel label="Deelnemers" waarde={eigenLeden.length} onder={`${openUitnodigingen.length} uitgenodigd`} />
            <Tegel
              label="Koploper"
              waarde={stand[0] ? stand[0].player.name : '—'}
              onder={stand[0] ? `${toonRating(stand[0].rating)} punten` : ''}
            />
            <Tegel
              label="Jouw plek"
              waarde={mijnPlek ? `${mijnPlek}e` : '—'}
              onder={doelpunten ? `${doelpunten} doelpunten in deze poule` : 'nog niets gespeeld'}
            />
          </div>

          <Kaart>
            <KaartKop
              titel="Stand"
              hint={
                <Link to="/ranglijst" className="hover:underline">
                  hele ranglijst →
                </Link>
              }
            />
            {stand.length ? (
              <Tabel koppen={['#', 'Speler', 'Rating', 'G–V', 'Winst']}>
                {stand.slice(standBegin, standBegin + STAND_PER_BLADZIJDE).map((r, i) => (
                  <Rij key={r.player.id}>
                    <Cel className="w-8">
                      <Plek nr={plaatsen[standBegin + i]} gedeeld={deeltPlek(plaatsen, standBegin + i)} />
                    </Cel>
                    <Cel>
                      <span className="flex items-center gap-2">
                        <Link to={`/speler/${r.player.id}`} className="font-medium hover:underline">
                          {toonNaam(r.player)}
                        </Link>
                        {r.player.bijnaam ? (
                          <span className="truncate text-xs text-krijt-500" title="bijnaam">
                            &ldquo;{r.player.bijnaam}&rdquo;
                          </span>
                        ) : null}
                        {r.player.id === profile?.id ? <span className="text-xs text-krijt-500">(jij)</span> : null}
                      </span>
                    </Cel>
                    <Cel nummer>
                      <strong>{toonRating(r.rating)}</strong>
                    </Cel>
                    <Cel nummer>
                      {r.stats.gewonnen}–{r.stats.verloren}
                    </Cel>
                    <Cel nummer>{Math.round(r.stats.winstPct)}%</Cel>
                  </Rij>
                ))}
              </Tabel>
            ) : (
              <Leeg tekst="Nog geen stand; er is nog niet gespeeld in deze poule." ikoon="ranglijst" />
            )}
            {stand.length > STAND_PER_BLADZIJDE ? (
              <Paginering
                totaal={stand.length}
                perPagina={STAND_PER_BLADZIJDE}
                pagina={standBlad}
                onPagina={setStandPagina}
              />
            ) : null}
          </Kaart>

          <div className="grid gap-3.5 xl:grid-cols-2">
            <Kaart>
              <KaartKop titel="Ratingverloop" hint="de bovenste vier" />
              {reeksen.length ? (
                <LijnGrafiek reeksen={reeksen} hoogte={200} basis={START_RATING} />
              ) : (
                <Leeg tekst="Te weinig potjes voor een verloop." ikoon="stijging" />
              )}
            </Kaart>

            <Kaart>
              <KaartKop titel={seizoenLabel(seizoen)} hint="dit kwartaal" />
              {seizoensRijen.length ? (
                <Tabel koppen={['#', 'Speler', 'Rating', 'Potjes']}>
                  {seizoensRijen.slice(0, 5).map((r, i) => (
                    <Rij key={r.player.id}>
                      <Cel className="w-8">
                        <Plek nr={plaatsenSeizoen[i]} gedeeld={deeltPlek(plaatsenSeizoen, i)} />
                      </Cel>
                      <Cel>{toonNaam(r.player)}</Cel>
                      <Cel nummer>{toonRating(r.rating)}</Cel>
                      <Cel nummer>{r.stats.gespeeld}</Cel>
                    </Rij>
                  ))}
                </Tabel>
              ) : (
                <Leeg tekst="Dit kwartaal nog niet gespeeld." ikoon="kalender" />
              )}
            </Kaart>
          </div>

          <Kaart>
            <KaartKop
              titel="Laatste potjes"
              hint={
                <Link to="/historie" className="hover:underline">
                  hele historie →
                </Link>
              }
            />
            {laatste.length ? (
              laatste
                .slice(0, 6)
                .map((m) => (
                  <WedstrijdRegel key={m.id} potje={m} zoekSpeler={zoekSpeler} delta={elo.perPotje[m.id]?.deltaA} deltaB={elo.perPotje[m.id]?.deltaB} />
                ))
            ) : (
              <Leeg tekst="Nog geen uitslagen in deze poule." ikoon="bal" />
            )}
          </Kaart>
        </div>

        {/* ---------- deelnemers, klein en rechts ---------- */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="border border-krijt-300 bg-white">
            <div className="flex border-b border-krijt-300">
              {(['deelnemers', 'bijnamen', 'uitnodigen'] as Tab[])
                .filter((t) => t !== 'uitnodigen' || ikBenBeheerder)
                .map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`flex-1 px-3 py-2.5 text-[13px] font-semibold transition ${
                      tab === t ? 'bg-krijt-950 text-white' : 'text-krijt-600 hover:bg-krijt-100'
                    }`}
                  >
                    {t === 'deelnemers'
                      ? `Deelnemers (${eigenLeden.length})`
                      : t === 'bijnamen'
                        ? 'Bijnamen'
                        : 'Uitnodigen'}
                  </button>
                ))}
            </div>

            {tab === 'deelnemers' ? (
              <div className="max-h-[520px] overflow-y-auto">
                {eigenLeden.map((l) => {
                  const plek = stand.findIndex((r) => r.player.id === l.profielId) + 1;
                  return (
                    <div key={l.profielId} className="flex items-center gap-2.5 border-b border-krijt-200 px-3 py-2.5 last:border-b-0">
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-krijt-950 text-[11px] font-bold text-white">
                        {initialen(naamVan(l.profielId))}
                      </span>
                      <span className="min-w-0 flex-1">
                        <Link to={`/speler/${l.profielId}`} className="block truncate text-sm font-medium hover:underline">
                          {naamVan(l.profielId)}
                        </Link>
                        <span className="text-xs text-krijt-500">
                          {plek ? `${plek}e` : 'nog niet gespeeld'}
                          {l.rol === 'beheerder' ? ' · beheerder' : ''}
                        </span>
                      </span>
                      {ikBenBeheerder && l.profielId !== profile?.id ? (
                        <LidKnoppen pouleId={id} lid={l} />
                      ) : null}
                    </div>
                  );
                })}

                {openUitnodigingen.length ? (
                  <div className="border-t border-krijt-200 bg-krijt-50 px-3 py-2.5">
                    <p className="opschrift mb-1.5 text-krijt-500">Uitgenodigd</p>
                    <div className="flex flex-wrap gap-1.5">
                      {openUitnodigingen.map((u) => (
                        <span key={u.id} className="border border-krijt-300 bg-white px-2 py-0.5 text-xs">
                          {naamVan(u.profielId)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : tab === 'bijnamen' ? (
              <BijnamenPaneel pouleId={id} leden={eigenLeden} />
            ) : (
              <UitnodigenPaneel pouleId={id} />
            )}
          </div>

          <p className="mt-2 text-xs leading-relaxed text-krijt-500">
            Alleen deelnemers kunnen worden opgesteld in een potje van deze poule.
          </p>
        </div>
      </div>

      {bewerken ? (
        <PouleModaal
          poule={poule}
          onSluit={() => setBewerken(false)}
          onBewaar={async (velden) => {
            try {
              await wijzigen.mutateAsync({ id, velden });
              meld('Poule bijgewerkt.');
              setBewerken(false);
            } catch (err) {
              meld(err instanceof Error ? err.message : 'Opslaan mislukte.', true);
            }
          }}
        />
      ) : null}

      {weg ? (
        <Bevestig
          titel="Poule verwijderen?"
          tekst="De poule verdwijnt met alle potjes en toernooien die erbij horen. Dat is niet terug te draaien."
          knop="Verwijderen"
          onNee={() => setWeg(false)}
          onJa={async () => {
            try {
              await verwijderen.mutateAsync(id);
              meld('Poule verwijderd.');
              navigeer('/poules');
            } catch (err) {
              meld(err instanceof Error ? err.message : 'Verwijderen mislukte.', true);
            }
            setWeg(false);
          }}
        />
      ) : null}
    </>
  );
}

function LidKnoppen({ pouleId, lid }: { pouleId: string; lid: { profielId: string; rol: 'beheerder' | 'lid' } }) {
  const rolZetten = useZetLidRol();
  const lidWeg = useVerwijderLid();

  return (
    <span className="flex shrink-0 gap-1">
      <button
        title={lid.rol === 'beheerder' ? 'Naar lid' : 'Maak beheerder'}
        onClick={() =>
          void rolZetten.mutateAsync({
            pouleId,
            profielId: lid.profielId,
            rol: lid.rol === 'beheerder' ? 'lid' : 'beheerder'
          })
        }
        className="border border-krijt-300 px-1.5 py-0.5 text-xs hover:bg-krijt-100"
      >
        {lid.rol === 'beheerder' ? '↓' : '↑'}
      </button>
      <button
        title="Uit de poule"
        onClick={() => void lidWeg.mutateAsync({ pouleId, profielId: lid.profielId })}
        className="border border-krijt-300 px-1.5 py-0.5 text-xs text-kritiek hover:bg-red-50"
      >
        <IcoonKruis />
      </button>
    </span>
  );
}

/*
 * Bijnamen. Iedereen mag er een geven aan iemand anders in de poule, en de
 * nieuwste overschrijft de vorige — er is er altijd maar één. Wie hem gaf staat
 * erbij; dat houdt het speels in plaats van anoniem.
 */
function BijnamenPaneel({ pouleId, leden }: { pouleId: string; leden: { profielId: string }[] }) {
  const { zoekSpeler } = useLeague(pouleId);
  const { profile } = useAuth();
  const zetten = useZetBijnaam();
  const meld = useToast();
  const [bezig, setBezig] = useState<string | null>(null);
  const [concept, setConcept] = useState<Record<string, string>>({});

  const anderen = leden.filter((l) => l.profielId !== profile?.id);

  async function bewaar(spelerId: string, waarde: string) {
    setBezig(spelerId);
    try {
      await zetten.mutateAsync({ spelerId, bijnaam: waarde });
      setConcept((oud) => {
        const nieuw = { ...oud };
        delete nieuw[spelerId];
        return nieuw;
      });
      meld(waarde.trim() ? 'Bijnaam opgeslagen.' : 'Bijnaam weggehaald.');
    } catch (err) {
      meld(err instanceof Error ? err.message : 'Opslaan mislukte.', true);
    }
    setBezig(null);
  }

  if (!anderen.length) {
    return (
      <div className="px-3 py-6">
        <Leeg tekst="Er is nog niemand anders in deze poule om een bijnaam te geven." ikoon="spelers" />
      </div>
    );
  }

  return (
    <div className="max-h-[520px] overflow-y-auto">
      <p className="border-b border-krijt-200 bg-krijt-50 px-3 py-2 text-xs text-krijt-600">
        Iedereen heeft er hoogstens één; wie een nieuwe geeft, overschrijft de vorige. Jezelf een bijnaam
        geven kan niet.
      </p>

      {anderen.map((l) => {
        const speler = zoekSpeler(l.profielId);
        const huidig = speler.bijnaam ?? '';
        const waarde = concept[l.profielId] ?? huidig;
        const gewijzigd = waarde.trim() !== huidig.trim();
        return (
          <div key={l.profielId} className="border-b border-krijt-200 px-3 py-2.5 last:border-b-0">
            <div className="flex items-center gap-2.5">
              <Avatar speler={speler} maat="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{toonNaam(speler)}</span>
                {speler.bijnaam ? (
                  <span className="text-xs text-krijt-500">
                    heet nu &ldquo;{speler.bijnaam}&rdquo;
                    {speler.bijnaamDoor ? ` · van ${toonNaam(zoekSpeler(speler.bijnaamDoor))}` : ''}
                  </span>
                ) : (
                  <span className="text-xs text-krijt-400">nog geen bijnaam</span>
                )}
              </span>
            </div>

            <div className="mt-2 flex gap-2">
              <input
                className="veld flex-1"
                maxLength={24}
                placeholder="Geef een bijnaam…"
                value={waarde}
                onChange={(e) => setConcept((oud) => ({ ...oud, [l.profielId]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && gewijzigd) void bewaar(l.profielId, waarde);
                }}
              />
              <Knop
                klein
                variant="primair"
                disabled={!gewijzigd || bezig === l.profielId}
                onClick={() => void bewaar(l.profielId, waarde)}
              >
                {bezig === l.profielId ? '…' : 'Opslaan'}
              </Knop>
              {speler.bijnaam ? (
                <Knop klein disabled={bezig === l.profielId} onClick={() => void bewaar(l.profielId, '')}>
                  Weg
                </Knop>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UitnodigenPaneel({ pouleId }: { pouleId: string }) {
  const uitnodigen = useNodigUit();
  const meld = useToast();
  const [naam, setNaam] = useState('');
  const [fout, setFout] = useState<string | null>(null);

  async function nodigUit() {
    const schoon = naam.trim().toLowerCase();
    if (!schoon) return;
    setFout(null);
    try {
      await uitnodigen.mutateAsync({ pouleId, gebruikersnaam: schoon });
      meld(`@${schoon} is uitgenodigd; die ziet het bij zijn meldingen.`);
      setNaam('');
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Uitnodigen mislukte.');
    }
  }

  return (
    <div className="p-3">
      {fout ? <div className="mb-2 border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-900">{fout}</div> : null}
      <div className="flex items-center gap-1.5">
        <span className="text-krijt-400">@</span>
        <input
          className="veld"
          value={naam}
          placeholder="gebruikersnaam"
          onChange={(e) => setNaam(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void nodigUit();
          }}
        />
      </div>
      <Knop
        variant="primair"
        className="mt-2 w-full justify-center"
        onClick={() => void nodigUit()}
        disabled={uitnodigen.isPending}
      >
        {uitnodigen.isPending ? 'Bezig…' : 'Uitnodigen'}
      </Knop>
      <p className="mt-2 text-xs leading-relaxed text-krijt-500">
        Je hebt geen e-mailadres nodig, en dat van de ander komt zo ook nooit in jouw browser. Diegene ziet de
        uitnodiging bij zijn meldingen.
      </p>
    </div>
  );
}

function PouleModaal({
  poule,
  onSluit,
  onBewaar
}: {
  poule: { naam: string; omschrijving: string | null; prijs: string | null };
  onSluit: () => void;
  onBewaar: (velden: { naam: string; omschrijving: string | null; prijs: string | null }) => Promise<void>;
}) {
  const [naam, setNaam] = useState(poule.naam);
  const [omschrijving, setOmschrijving] = useState(poule.omschrijving ?? '');
  const [prijs, setPrijs] = useState(poule.prijs ?? '');

  return (
    <Modaal
      titel="Poule bewerken"
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop
            variant="primair"
            onClick={() =>
              void onBewaar({
                naam: naam.trim(),
                omschrijving: omschrijving.trim() || null,
                prijs: prijs.trim() || null
              })
            }
          >
            Opslaan
          </Knop>
        </>
      }
    >
      <Veld label="Naam">
        <input className="veld" value={naam} onChange={(e) => setNaam(e.target.value)} />
      </Veld>
      <Veld label="Omschrijving">
        <input className="veld" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
      </Veld>
      <Veld label="Prijs" help="Wat wint de winnaar?">
        <input className="veld" value={prijs} onChange={(e) => setPrijs(e.target.value)} />
      </Veld>
      <p className="text-xs text-krijt-500">Aangemaakt op {fmtDatum(new Date())} — deelnemers beheer je rechts.</p>
    </Modaal>
  );
}
